/*****************************************************
 * Packages
 ******************************************************/
import React, { useState, useRef, useCallback } from 'react';
import styled from 'styled-components';
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

/*****************************************************
 * Locals
 ******************************************************/
import GroupBody from '../core/GroupBody';
import ModalTab, { TabOption } from './ModalTab';
import EditorModalBase from './EditorModalBase';
import Accordion, {
    AccordionItem,
    AccordionDetails,
    AccordionSummary,
} from '../../other-components/Accordion';

const ModalFooter = styled.div`
    left: 0;
    right: 0;
    bottom: 0;
    font-size: 14px;
    position: absolute;
    color: ${({ theme }) => theme.bodyText};

    a {
        color: ${({ theme }) => theme.bodyText};
        &:hover {
            color: ${({ theme }) => theme.primary.fg};
        }
    }

    display: flex;
    height: 40px;
    padding: 0 20px;
    align-items: center;
    justify-content: center;
    border-radius: 0 0 5px 5px;
    background: ${({ theme }) => theme.primary.bg};
    border-top: 2px solid ${({ theme }) => theme.inputBorder};

    .undo-redo {
        padding-left: 8px;
        padding-right: 8px;
    }

    .icon {
        color: ${({ theme }) => theme.primary.fg};
    }
`;

const EditorModal = (props) => {
    const simpleBarRef = useRef();
    const { close, settings, onSaveSettings, editElType, title } = props;

    const [activeTab, setActiveTab] = useState(0);
    const handleTabChange = React.useCallback(
        (payload) => setActiveTab(payload.value),
        []
    );
    const settingsContent = React.useMemo(
        () => settings[activeTab].getContentByType(editElType),
        [activeTab, editElType, settings]
    );

    const handleExpend = useCallback((e) => {
        const { offsetTop } = e.target.closest('.AccordionSummary');
        if (simpleBarRef.current) {
            setTimeout(() => {
                simpleBarRef.current
                    .getScrollElement()
                    .scrollTo({ top: offsetTop, behavior: 'smooth' });
            }, 0);
        }
    }, []);

    return (
        <EditorModalBase
            hasBackdrop={false}
            close={close}
            title={title}
            size="sm"
        >
            <ModalTab
                type="underlined"
                selected={activeTab}
                onSelect={handleTabChange}
                style={{ textTransform: 'uppercase' }}
            >
                {settings.map((option, index) => (
                    <TabOption
                        value={index}
                        key={option.title}
                        icon={option.icon}
                    >
                        {option.title}
                    </TabOption>
                ))}
            </ModalTab>
            <SimpleBar ref={simpleBarRef} style={{ maxHeight: 513 }}>
                <Accordion
                    // toggleable
                    onExpend={handleExpend}
                    collapsedIcon="plus"
                    expendedIcon="minus"
                >
                    {settingsContent.map((group) => {
                        return (
                            <AccordionItem key={group.label}>
                                <AccordionSummary className="AccordionSummary">
                                    {group.label}
                                </AccordionSummary>
                                <AccordionDetails>
                                    <GroupBody
                                        group={group}
                                        handleChange={onSaveSettings}
                                    />
                                </AccordionDetails>
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            </SimpleBar>
            <ModalFooter>
                Need help?&nbsp;
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://help.dorik.io"
                >
                    Read Documentation
                </a>
            </ModalFooter>
        </EditorModalBase>
    );
};

const EditorModalDragable = (props) => {
    return (
        <DndProvider backend={HTML5Backend}>
            <EditorModal {...props} />
        </DndProvider>
    );
};

export default React.memo(EditorModalDragable);
